import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { X, Music, Check, Loader2 } from 'lucide-react';
import { getToken } from '@/lib/auth';

interface SpotifyPlaylist {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  track_count: number;
  is_public: boolean;
  owner_name?: string;
}

interface PlaylistImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

/**
 * PlaylistImportModal Component
 *
 * Allows users to select and import playlists from their Spotify account.
 * Features:
 * - Grid view of playlists with cover art
 * - Multi-select with checkboxes
 * - Search/filter functionality
 * - Import progress tracking
 */
export const PlaylistImportModal: React.FC<PlaylistImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
}) => {
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch user's Spotify playlists
  useEffect(() => {
    if (isOpen) {
      fetchPlaylists();
    }
  }, [isOpen]);

  const fetchPlaylists = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await fetch('/automusic/api/playlists/spotify', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch playlists');
      }

      const data = await response.json();
      setPlaylists(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlaylist = (playlistId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(playlistId)) {
      newSelected.delete(playlistId);
    } else {
      newSelected.add(playlistId);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    const filtered = getFilteredPlaylists();
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(p => p.id)));
    }
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) return;

    setImporting(true);
    setError(null);

    try {
      const token = getToken();
      const response = await fetch('/automusic/api/playlists/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          playlist_ids: Array.from(selectedIds),
          import_tracks: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to import playlists');
      }

      const result = await response.json();
      console.log(`Successfully imported ${result.imported_count} playlists`);

      onImportComplete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import playlists');
    } finally {
      setImporting(false);
    }
  };

  const getFilteredPlaylists = () => {
    if (!searchQuery) return playlists;

    const query = searchQuery.toLowerCase();
    return playlists.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query) ||
      p.owner_name?.toLowerCase().includes(query)
    );
  };

  if (!isOpen) return null;

  const filteredPlaylists = getFilteredPlaylists();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-white">Import Playlists</h2>
            <p className="text-gray-400 text-sm mt-1">
              Select playlists from your Spotify account to import
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition"
            disabled={importing}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-800">
          <input
            type="text"
            placeholder="Search playlists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-spotify-green"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-spotify-green" size={48} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Music className="text-red-500 mb-4" size={48} />
              <p className="text-red-400 mb-2">{error}</p>
              <button
                onClick={fetchPlaylists}
                className="text-spotify-green hover:underline"
              >
                Try Again
              </button>
            </div>
          ) : filteredPlaylists.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Music className="text-gray-500 mb-4" size={48} />
              <p className="text-gray-400">No playlists found</p>
            </div>
          ) : (
            <>
              {/* Select All Button */}
              <div className="mb-4 flex items-center justify-between">
                <button
                  onClick={handleSelectAll}
                  className="text-spotify-green hover:underline text-sm"
                >
                  {selectedIds.size === filteredPlaylists.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-gray-400 text-sm">
                  {selectedIds.size} selected
                </span>
              </div>

              {/* Playlist Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredPlaylists.map((playlist) => (
                  <div
                    key={playlist.id}
                    onClick={() => handleTogglePlaylist(playlist.id)}
                    className={clsx(
                      'relative cursor-pointer rounded-lg overflow-hidden transition-all',
                      'bg-gray-800 hover:bg-gray-750 group',
                      {
                        'ring-2 ring-spotify-green': selectedIds.has(playlist.id),
                      }
                    )}
                  >
                    {/* Cover Image */}
                    <div className="aspect-square relative">
                      {playlist.image_url ? (
                        <img
                          src={playlist.image_url}
                          alt={playlist.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-700">
                          <Music className="text-gray-500" size={48} />
                        </div>
                      )}

                      {/* Checkbox Overlay */}
                      <div
                        className={clsx(
                          'absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center transition',
                          {
                            'bg-spotify-green': selectedIds.has(playlist.id),
                            'bg-gray-900 bg-opacity-70 group-hover:bg-opacity-90': !selectedIds.has(playlist.id),
                          }
                        )}
                      >
                        {selectedIds.has(playlist.id) && (
                          <Check className="text-black" size={16} />
                        )}
                      </div>
                    </div>

                    {/* Playlist Info */}
                    <div className="p-3">
                      <h3 className="font-semibold text-white text-sm truncate">
                        {playlist.name}
                      </h3>
                      <p className="text-gray-400 text-xs mt-1">
                        {playlist.track_count} tracks
                      </p>
                      {playlist.owner_name && (
                        <p className="text-gray-500 text-xs truncate">
                          by {playlist.owner_name}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex items-center justify-between">
          <div className="text-gray-400 text-sm">
            {selectedIds.size > 0 && (
              <span>{selectedIds.size} playlist{selectedIds.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={importing}
              className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={selectedIds.size === 0 || importing}
              className={clsx(
                'px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2',
                {
                  'bg-spotify-green text-black hover:bg-green-400': selectedIds.size > 0 && !importing,
                  'bg-gray-700 text-gray-500 cursor-not-allowed': selectedIds.size === 0 || importing,
                }
              )}
            >
              {importing ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Importing...
                </>
              ) : (
                `Import ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistImportModal;
